import pictogramAsset from "assets/img/pictogram/asset.svg"
import IconHierarchyBranch from "assets/img/icon-hierarchy-branch.svg?react"

import { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import toastMessages from "stores/toastMessages"
import { Guid } from "@/types"
import { Grid, GridItem } from "components/Grid"
import { Flex } from "components/Flex"
import Asset, { IGetFreightUnit } from "models/Asset"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import Pictogram from "components/Pictogram/Pictogram"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import Lmid from "components/Lmid/Lmid"
import Text from "components/Text/Text"
import Checkbox from "components/Checkbox/Checkbox"
import Measurements from "components/Measurements/Measurements"
import { ShippingOrderCartItemsSelectionState } from "stores/shippingOrderCart"

export interface ISelectedFreightUnit {
    assetId: Guid
    freightUnit: IGetFreightUnit
}

interface ISelectFreightUnitsModalProps {
    assetsWithFreightUnits: Asset[]
    initialSelectedFreightUnits?: ISelectedFreightUnit[]
    onSave(selectedFreightUnits: ISelectedFreightUnit[]): Promise<void>
    onClose(): void
}

const SelectFreightUnitsModal: FC<ISelectFreightUnitsModalProps> = ({
    assetsWithFreightUnits,
    initialSelectedFreightUnits,
    onSave,
    onClose,
}) => {
    const { t } = useTranslation()
    const [isSaving, setIsSaving] = useState(false)
    const [selectedItems, setSelectedItems] = useState<{ assetId: Guid, freightUnitIndex: number }[]>([])

    const initialSelectedItems = useMemo<{ assetId: Guid, freightUnitIndex: number }[]>(() => {
        if (!initialSelectedFreightUnits?.length) {
            return []
        }

        return initialSelectedFreightUnits
            .map(item => {
                const asset = assetsWithFreightUnits.find(a => a.id === item.assetId)
                if (!asset) return null

                const freightUnitIndex = asset.freightUnits.findIndex(unit => {
                    return unit.description === item.freightUnit.description
                        && (!unit.measurementSystemId || !item.freightUnit.measurementSystemId || unit.measurementSystemId === item.freightUnit.measurementSystemId)
                        && (unit.dimension1 || "") === (item.freightUnit.dimension1 || "")
                        && (unit.dimension2 || "") === (item.freightUnit.dimension2 || "")
                        && (unit.dimension3 || "") === (item.freightUnit.dimension3 || "")
                        && (unit.weight || "") === (item.freightUnit.weight || "")
                })

                if (freightUnitIndex < 0) return null

                return {
                    assetId: item.assetId,
                    freightUnitIndex,
                }
            })
            .filter(i => !!i) as { assetId: Guid, freightUnitIndex: number }[]
    }, [assetsWithFreightUnits, initialSelectedFreightUnits])

    useEffect(() => {
        setSelectedItems(initialSelectedItems)
    }, [initialSelectedItems])

    async function handleSave() {
        setIsSaving(true)
        try {
            await onSave(
                selectedItems.map(item => {
                    const asset = assetsWithFreightUnits.find(a => a.id === item.assetId)
                    if (!asset) return null
                    const freightUnit = asset.freightUnits[item.freightUnitIndex]
                    if (!freightUnit) return null
                    const selectedFreightUnit: ISelectedFreightUnit = {
                        assetId: item.assetId,
                        freightUnit,
                    }
                    return selectedFreightUnit
                })
                .filter(i => i) as ISelectedFreightUnit[]
            )
        } catch(e) {
            toastMessages.showSystemError()
            setIsSaving(false)
        }
    }

    function isItemSelected(assetId: Guid, freightUnitIndex: number) {
        return selectedItems.some(item => {
            return item.assetId === assetId
                && item.freightUnitIndex === freightUnitIndex
        })
    }

    function toggleItemSelection(assetId: Guid, freightUnitIndex: number) {
        setSelectedItems(current => {
            if (current.some(item => item.assetId === assetId && item.freightUnitIndex === freightUnitIndex)) {
                return current.filter(item => !(item.assetId === assetId && item.freightUnitIndex === freightUnitIndex))
            } else {
                return [...current, { assetId, freightUnitIndex }]
            }
        })
    }

    function getAssetSelectionState(assetId: Guid, freightUnitsCount: number) {
        if (freightUnitsCount === 0) {
            return ShippingOrderCartItemsSelectionState.noneSelected
        }

        const selectedCount = selectedItems.filter(item => item.assetId === assetId).length
        if (selectedCount === 0) {
            return ShippingOrderCartItemsSelectionState.noneSelected
        }
        if (selectedCount === freightUnitsCount) {
            return ShippingOrderCartItemsSelectionState.allSelected
        }
        return ShippingOrderCartItemsSelectionState.someSelected
    }

    function toggleAssetSelection(assetId: Guid, freightUnitsCount: number) {
        const selectionState = getAssetSelectionState(assetId, freightUnitsCount)

        setSelectedItems(current => {
            if (selectionState === ShippingOrderCartItemsSelectionState.allSelected) {
                return current.filter(item => item.assetId !== assetId)
            }

            const currentIndexes = new Set(current
                .filter(item => item.assetId === assetId)
                .map(item => item.freightUnitIndex))

            const itemsToAdd: { assetId: Guid, freightUnitIndex: number }[] = []
            for (let freightUnitIndex = 0; freightUnitIndex < freightUnitsCount; freightUnitIndex++) {
                if (!currentIndexes.has(freightUnitIndex)) {
                    itemsToAdd.push({ assetId, freightUnitIndex })
                }
            }

            return [...current, ...itemsToAdd]
        })
    }

    return (
        <ConfirmModal
            title={t("freightUnit.selectFreightUnits")}
            maxWidth="95.2rem"
            onClose={onClose}
            confirmButtonLabel={t("common.save")}
            onConfirm={handleSave}
            isBusy={isSaving}>
                {t("freightUnit.selectFreightUnitsDescr")}
                {assetsWithFreightUnits.map(asset => (
                    <div key={asset.id}
                        className="mt-4 overflow-hidden border border-grey-100 round-sm">
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            className="bg-grey-50 px-2 py-1-5">
                            <Grid columns="4rem minmax(0, auto)" gap="1.6rem" alignItems="center">
                                <Pictogram
                                    size="4rem"
                                    imgSize="24px"
                                    src={pictogramAsset}
                                />
                                <ArticleDescription
                                    articleId={asset.articleId}
                                    articleDescription={asset.articleDescription}
                                />
                            </Grid>
                            <Lmid
                                assetId={asset.id}
                                lmid={asset.lmid}
                                labeled={asset.isLabeled}
                            />
                        </Flex>
                        <div className="pb-2">
                            <Text type="pSmallest"
                                className="mx-1 px-2 pt-1-5 pb-1 border-bottom-grey-100 color-grey-600 font-bold">
                                <Grid columns="max-content minmax(0, auto)" gap="0.8rem" alignItems="center">
                                    <Checkbox
                                        checked={getAssetSelectionState(asset.id!, asset.freightUnits.length) === ShippingOrderCartItemsSelectionState.allSelected}
                                        partiallyChecked={getAssetSelectionState(asset.id!, asset.freightUnits.length) === ShippingOrderCartItemsSelectionState.someSelected}
                                        onChange={() => toggleAssetSelection(asset.id!, asset.freightUnits.length)}
                                    />
                                    <span>{t("freightUnit.freightUnits")}</span>
                                </Grid>
                            </Text>
                            {asset.freightUnits.map((freightUnit, freightUnitIndex) => (
                                <Grid
                                    key={freightUnitIndex}
                                    columns="4rem minmax(0, auto) max-content"
                                    className="border-grey-100-between mx-3 py-1"
                                    alignItems="center">
                                    <Flex alignItems="center" justifyContent="center" className="w-100">
                                        <IconHierarchyBranch />
                                    </Flex>
                                    <GridItem className="pr-3">
                                        <Checkbox
                                            label={freightUnit.description}
                                            checked={isItemSelected(asset.id!, freightUnitIndex)}
                                            onChange={() => toggleItemSelection(asset.id!, freightUnitIndex)}
                                        />
                                    </GridItem>
                                    <Measurements
                                        measurementSystemId={freightUnit.measurementSystemId}
                                        dimension1={freightUnit.dimension1}
                                        dimension2={freightUnit.dimension2}
                                        dimension3={freightUnit.dimension3}
                                        volume={freightUnit.volume}
                                        weight={freightUnit.weight}
                                    />
                                </Grid>
                            ))}
                        </div>
                    </div>
                ))}
        </ConfirmModal>
    )
}

export default SelectFreightUnitsModal
